import { API_BASE_URL } from '@synergy/config';
import { request } from '@synergy/api';
import { useEffect, useState } from 'react';

/** How a field is compared, in the words the query string uses. */
export type PaloListFilterOperator =
    | 'eq'
    | 'neq'
    | 'contains'
    | 'starts'
    | 'in'
    | 'from'
    | 'until'
    | 'gt'
    | 'lt'
    | 'between'
    | 'empty'
    | 'notEmpty';

/** `money` is a number the screens write as an amount, in the list's currency. */
export type PaloListFilterType = 'text' | 'number' | 'money' | 'date' | 'choice' | 'reference' | 'flag';

export interface PaloListFilterField {
    key: string;
    type: PaloListFilterType;
    operators: PaloListFilterOperator[];
    /** The values a choice takes; null for every other kind. */
    choices: string[] | null;
}

/** What the list offers, until the answer arrives. */
const NONE: PaloListFilterField[] = [];

/**
 * What a list can be narrowed by, asked of the list itself.
 *
 * ERPNext keeps the same knowledge in a settings screen an administrator
 * fills in by hand, which the frame leaves out on purpose (#2206, rule 11).
 * Here the list declares its fields in code and hands them out, so a column
 * gets a filter because the server can answer it — not because someone
 * remembered to write one for it on this side as well (#2209).
 *
 * The answer does not change while anyone is looking at a list, so it is kept
 * for the life of the page.
 */
const cache = new Map<string, PaloListFilterField[]>();

export const usePaloListFilters = (listKey?: string): PaloListFilterField[] => {
    const [fields, setFields] = useState<PaloListFilterField[]>(() => (listKey ? cache.get(listKey) ?? NONE : NONE));

    useEffect(() => {
        if (!listKey) {
            setFields(NONE);

            return;
        }

        const known = cache.get(listKey);

        if (known) {
            setFields(known);

            return;
        }

        let current = true;

        void (async () => {
            const response = await request.get(`${API_BASE_URL}/list-filters/${listKey}`).catch(() => null);
            const loaded: PaloListFilterField[] = response?.data?.fields ?? NONE;

            cache.set(listKey, loaded);

            if (current) {
                setFields(loaded);
            }
        })();

        return () => {
            current = false;
        };
    }, [listKey]);

    return fields;
};

/**
 * The comparison a field is filtered with when nobody picks one.
 *
 * Text looks for the part, as the search field above a list in ERPNext does;
 * everything else is compared as it stands. A field that does not offer the
 * one its kind implies takes the first it does offer — the article number,
 * for instance, which can begin with something but not contain it.
 */
export const paloDefaultOperator = (field: PaloListFilterField): PaloListFilterOperator => {
    const wanted: PaloListFilterOperator = 'text' === field.type ? 'contains' : 'eq';

    return field.operators.includes(wanted) ? wanted : field.operators[0] ?? 'eq';
};

/** One value a list holds in a field, with the wording a person reads. */
export interface PaloListFilterValue {
    value: string;
    label: string;
}

const NO_VALUES: PaloListFilterValue[] = [];

const valueCache = new Map<string, PaloListFilterValue[]>();

/**
 * Which values a list actually holds in one of its fields.
 *
 * Asked of the list rather than built from somewhere else: the responsible
 * agents on offer used to be everyone attached to the POOLALPIN account,
 * which left eight names that find nothing and hid four people who handle
 * 1.209 inquiries between them (#2231 R-128). The answer does not change
 * while anyone is looking at a list, so it is kept for the life of the page.
 */
export const usePaloListFilterValues = (listKey?: string, field?: string): PaloListFilterValue[] => {
    const at = listKey && field ? `${listKey}/${field}` : undefined;
    const [values, setValues] = useState<PaloListFilterValue[]>(() =>
        at ? valueCache.get(at) ?? NO_VALUES : NO_VALUES
    );

    useEffect(() => {
        if (!at) {
            setValues(NO_VALUES);

            return;
        }

        const known = valueCache.get(at);

        if (known) {
            setValues(known);

            return;
        }

        let current = true;

        void (async () => {
            const response = await request.get(`${API_BASE_URL}/list-filters/${at}/values`).catch(() => null);
            const loaded: PaloListFilterValue[] = response?.data?.values ?? NO_VALUES;

            valueCache.set(at, loaded);

            if (current) {
                setValues(loaded);
            }
        })();

        return () => {
            current = false;
        };
    }, [at]);

    return values;
};
